import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { logger } from '../../lib/logger';
import { createProgressPoller } from '../../lib/progressPoller';

const PARSE_STAGE_LABELS = {
  reading_file: 'Reading file…',
  extracting_tables: 'Extracting tables…',
  ai_text_extraction: 'AI reading document text…',
  vision_ocr: 'AI scanning pages…',
  normalizing: 'Normalizing records…',
  processing: 'Processing…',
};

const buildParseProgress = (job) => {
  const p = job?.progress || {};
  const stage = p.stage || 'processing';
  let percent = 10;
  if (stage === 'reading_file') percent = 5;
  else if (stage === 'extracting_tables') percent = 15;
  else if (stage === 'ai_text_extraction') percent = 45;
  else if (stage === 'vision_ocr') {
    percent = p.total > 0 ? 20 + Math.round(70 * ((p.current || 0) / p.total)) : 30;
  } else if (stage === 'normalizing') percent = 95;
  let label = PARSE_STAGE_LABELS[stage] || 'Processing…';
  if (stage === 'vision_ocr' && p.total) {
    label = `AI scanning pages — ${p.current || 0} of ${p.total} done`;
  }
  return { stage, percent, label };
};

/**
 * Hook encapsulating the 3-step batch import flow:
 *   1. Upload    — collect file
 *   2. Parse     — POST /import/parse → check duplicates (staging area)
 *   3. Review    — POST /import/execute → show result
 *
 * Async PDF/Word parses poll the owner-bound job endpoint every 2s while
 * processing (throttled when the tab is hidden, stopped on terminal
 * states) and expose live `parseProgress`. The active job id is kept in
 * sessionStorage so a page refresh can resume the running parse.
 */
export const useBatchImport = ({ importType, isOpen, onImportComplete, onClose }) => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseProgress, setParseProgress] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [duplicateCheck, setDuplicateCheck] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showAllFields, setShowAllFields] = useState(false);
  const pollerRef = useRef(null);
  const resumeTriedRef = useRef(false);

  const storageKey = `azai_import_job_${importType}`;

  const reset = () => {
    setStep(1);
    setFile(null);
    setParsedData(null);
    setDuplicateCheck(null);
    setImportResult(null);
    setShowAllFields(false);
    setParseProgress(null);
  };

  const handleClose = () => {
    // Stop the poll loop; a server-side parse keeps running and can be
    // resumed when the dialog is reopened (job id stays in storage).
    pollerRef.current?.stop();
    pollerRef.current = null;
    reset();
    onClose?.();
  };

  useEffect(() => () => pollerRef.current?.stop(), []);

  const _waitForJob = useCallback(
    (jobId) =>
      new Promise((resolve, reject) => {
        pollerRef.current?.stop();
        pollerRef.current = createProgressPoller({
          fetchStatus: async () => {
            const res = await api.get(`/import/parse-status/${jobId}`);
            return {
              terminal: res.data.status === 'done' || res.data.status === 'error',
              data: res.data,
            };
          },
          onUpdate: (job) => {
            if (job.status === 'processing') setParseProgress(buildParseProgress(job));
          },
          onDone: (job) => {
            if (job.status === 'done') {
              resolve(job.result);
            } else {
              const err = new Error(job.error || 'Parse failed');
              err.response = { data: { detail: job.error || 'Failed to parse file' } };
              reject(err);
            }
          },
          onError: (err) => {
            if (err?.response?.status === 404) {
              const e = new Error('Parse job not found');
              e.response = { data: { detail: 'Parse job not found — please retry.' } };
              reject(e);
              return;
            }
            if (err?.response) {
              reject(err);
              return;
            }
            const e = new Error('Parse timed out');
            e.response = {
              data: {
                detail:
                  'Parsing took longer than expected — try splitting the PDF in half and uploading each part.',
              },
            };
            reject(e);
          },
          intervalMs: 2000,
          hiddenIntervalMs: 10000,
          maxDurationMs: 10 * 60 * 1000,
        });
      }),
    []
  );

  const _afterParse = useCallback(
    async (parseResult) => {
      sessionStorage.removeItem(storageKey);
      setParsedData(parseResult);
      if (parseResult.ai_fallback_used) {
        toast.info(
          'AI extracted the records from this document — please review carefully on the next step.',
          { duration: 6000 }
        );
      }
      if (parseResult.valid_records > 0) {
        const dupResponse = await api.post('/import/check-duplicates', {
          records: parseResult.records,
          import_type: importType,
        });
        setDuplicateCheck(dupResponse.data);
      }
      setStep(2);
    },
    [importType, storageKey]
  );

  const _handleParseError = (error) => {
    const isTimeout =
      error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '');
    if (isTimeout) {
      toast.error(
        'Parsing took longer than expected — this can happen for very large scanned rosters. Try splitting the PDF in half and uploading each part.',
        { duration: 9000 }
      );
    } else {
      toast.error(error.response?.data?.detail || 'Failed to parse file');
    }
  };

  // Refresh recovery: when the dialog opens and a parse job for this
  // import type is still pending from before the refresh, resume polling
  // instead of forcing a re-upload.
  useEffect(() => {
    if (!isOpen || resumeTriedRef.current) return;
    resumeTriedRef.current = true;
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    } catch {
      saved = null;
    }
    if (!saved?.job_id) return;
    setParsing(true);
    setParseProgress({ stage: 'processing', percent: 10, label: 'Resuming parse…' });
    toast.info(`Resuming parse of "${saved.filename || 'your file'}"…`);
    _waitForJob(saved.job_id)
      .then((parseResult) => _afterParse(parseResult))
      .catch((error) => {
        sessionStorage.removeItem(storageKey);
        _handleParseError(error);
      })
      .finally(() => {
        setParsing(false);
        setParseProgress(null);
      });
  }, [isOpen, storageKey, _waitForJob, _afterParse]);

  const handleParse = async () => {
    if (!file) return;
    setParsing(true);
    setParseProgress(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      // Scanned PDFs may need ~1-3 min for vision OCR (cascaded fallback).
      // The actual long parse runs as a background job (see _waitForJob).
      const response = await api.post(`/import/parse/${importType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 5 * 60 * 1000,
      });
      let parseResult = response.data;
      if (parseResult?.async && parseResult.job_id) {
        // PDFs/Word: poll the owner-bound job until it completes.
        sessionStorage.setItem(
          storageKey,
          JSON.stringify({ job_id: parseResult.job_id, filename: file.name })
        );
        setParseProgress({ stage: 'reading_file', percent: 5, label: 'Reading file…' });
        parseResult = await _waitForJob(parseResult.job_id);
      }
      await _afterParse(parseResult);
    } catch (error) {
      sessionStorage.removeItem(storageKey);
      _handleParseError(error);
    } finally {
      setParsing(false);
      setParseProgress(null);
    }
  };

  const handleImport = async () => {
    if (!duplicateCheck?.records_to_import?.length) {
      toast.error('No records to import');
      return;
    }
    setImporting(true);
    try {
      const response = await api.post('/import/execute', {
        records: duplicateCheck.records_to_import,
        import_type: importType,
        skip_duplicates: true,
      });
      setImportResult(response.data);
      setStep(3);
      if (response.data.imported_count > 0) {
        toast.success(`Imported ${response.data.imported_count} ${importType}s`);
        onImportComplete?.();
      }
    } catch (e) {
      logger.error('[BatchImport] import failed', e);
      toast.error('Import failed');
    } finally {
      setImporting(false);
    }
  };

  return {
    step,
    setStep,
    file,
    setFile,
    parsing,
    parseProgress,
    parsedData,
    duplicateCheck,
    importing,
    importResult,
    showAllFields,
    setShowAllFields,
    handleClose,
    handleParse,
    handleImport,
  };
};
